import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import PostContent from "./components/PostContent";
import PostSidebar from "./components/PostSidebar";
import { blogPosts } from "../../mocks/blogPosts";

export default function PostPage() {
  const { id } = useParams<{ id: string }>();
  const post = blogPosts.find((p) => p.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = blogPosts.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* Hero Image */}
      <div className="relative w-full h-[55vh] min-h-[420px] bg-[#111] overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/20" />

        {/* Hero Text */}
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-10 pb-12">
          <div className="max-w-7xl mx-auto">
            <span
              className="bg-white text-black text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 font-bold inline-block mb-5"
              style={{ fontFamily: "'Barlow', sans-serif" }}
            >
              {post.category}
            </span>
            <h1
              className="text-white font-black text-4xl md:text-5xl lg:text-6xl leading-tight uppercase max-w-3xl"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Article + Sidebar */}
      <main className="flex-1 py-16 px-6 md:px-10">
        <div className="max-w-7xl mx-auto flex gap-14 items-start">

          {/* Article */}
          <article className="flex-1 min-w-0">
            {/* Breadcrumb */}
            <nav
              className="flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-black/30 mb-10"
              style={{ fontFamily: "'Barlow', sans-serif" }}
            >
              <Link to="/" className="hover:text-black transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <Link to="/blog" className="hover:text-black transition-colors">Blog</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <span className="text-black truncate max-w-[200px]">{post.category}</span>
            </nav>

            {/* Meta */}
            <div className="flex items-center gap-4 mb-10 pb-10 border-b border-black/10">
              <span className="text-black/40 text-xs" style={{ fontFamily: "'Barlow', sans-serif" }}>
                {post.date}
              </span>
              <span className="w-1 h-1 bg-black/20 rounded-full" />
              <span className="text-black/40 text-xs" style={{ fontFamily: "'Barlow', sans-serif" }}>
                {post.readTime}
              </span>
            </div>

            {/* Excerpt */}
            <p
              className="text-black text-xl leading-relaxed mb-10 font-medium border-l-2 border-black pl-6"
              style={{ fontFamily: "'Barlow', sans-serif" }}
            >
              {post.excerpt}
            </p>

            {/* Body */}
            <PostContent content={post.content as { type: "p" | "h2"; text: string }[]} />

            {/* Back */}
            <div className="mt-16 pt-10 border-t border-black/10">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-black border border-black px-6 py-3 hover:bg-black hover:text-white transition-all duration-200 rounded-sm whitespace-nowrap"
                style={{ fontFamily: "'Barlow', sans-serif" }}
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                Back to All Stories
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <PostSidebar currentId={post.id} />
        </div>
      </main>

      {/* Related Posts */}
      <section className="bg-[#f6f6f6] py-16 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <p
            className="text-black/30 text-[10px] tracking-[0.35em] uppercase mb-8"
            style={{ fontFamily: "'Barlow', sans-serif" }}
          >
            More Stories
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link key={p.id} to={`/blog/${p.id}`} className="group block">
                <article className="bg-white rounded-sm overflow-hidden border border-black/8 hover:-translate-y-1 transition-all duration-300">
                  <div className="relative w-full h-44 overflow-hidden bg-[#e8e8e8]">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-0 left-0">
                      <span
                        className="bg-black text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 font-bold"
                        style={{ fontFamily: "'Barlow', sans-serif" }}
                      >
                        {p.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-black/40 text-[11px]" style={{ fontFamily: "'Barlow', sans-serif" }}>
                        {p.date}
                      </span>
                      <span className="w-1 h-1 bg-black/20 rounded-full" />
                      <span className="text-black/40 text-[11px]" style={{ fontFamily: "'Barlow', sans-serif" }}>
                        {p.readTime}
                      </span>
                    </div>
                    <h3
                      className="text-black font-bold text-base leading-tight group-hover:opacity-70 transition-opacity"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {p.title}
                    </h3>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
